import Phaser from 'phaser'
import { getAvailableWallets, connectWallet, getPublicKey } from '../chain/wallet'
import type { WalletType } from '../chain/wallet'
import { fetchPlayerState } from '../chain/player'
import { fetchRunState, initPlayer, initRun, finishRun, isRunStateDelegated, undelegateRun, waitForUndelegation } from '../chain/run'
import { cachePlayerState, cacheRunState } from '../systems/GameState'
import { getConnection } from '../chain/connection'
import { getCluster } from '../chain/connection'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'

const GAME_W = 720
const GAME_H = 1280

export default class BootScene extends Phaser.Scene {
  private statusText!: Phaser.GameObjects.Text

  constructor() {
    super('BootScene')
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0x1a1a2e)

    // Devnet banner
    if (getCluster() === 'devnet') {
      this.add.rectangle(GAME_W / 2, GAME_H - 16, GAME_W, 32, 0xcc0000).setDepth(999)
      this.add.text(GAME_W / 2, GAME_H - 16, 'DEVNET', {
        fontSize: '18px', fontFamily: 'monospace', color: '#FFFFFF', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(1000)
    }

    // Title
    this.add.text(GAME_W / 2, GAME_H / 2 - 200, 'DRILL IDLE', {
      fontSize: '64px', fontFamily: 'monospace', color: '#FFD700', fontStyle: 'bold',
    }).setOrigin(0.5)

    this.add.text(GAME_W / 2, GAME_H / 2 - 120, 'on Solana', {
      fontSize: '28px', fontFamily: 'monospace', color: '#8888AA',
    }).setOrigin(0.5)

    // Status text
    this.statusText = this.add.text(GAME_W / 2, GAME_H / 2 + 240, '', {
      fontSize: '24px', fontFamily: 'monospace', color: '#AAAAAA',
    }).setOrigin(0.5)

    // Wallet selection buttons
    const wallets = getAvailableWallets()
    if (wallets.length === 0) {
      this.setStatus('No wallets found. Install Phantom or Solflare.')
      return
    }

    this.add.text(GAME_W / 2, GAME_H / 2 - 40, 'Select a wallet', {
      fontSize: '26px', fontFamily: 'monospace', color: '#CCCCCC',
    }).setOrigin(0.5)

    let y = GAME_H / 2 + 30
    for (const wallet of wallets) {
      const btn = this.add.rectangle(GAME_W / 2, y, 420, 70, 0x4444AA)
        .setStrokeStyle(3, 0x8888DD)
        .setInteractive({ useHandCursor: true })

      this.add.text(GAME_W / 2, y, wallet.name, {
        fontSize: '30px', fontFamily: 'monospace', color: '#FFFFFF', fontStyle: 'bold',
      }).setOrigin(0.5)

      btn.on('pointerover', () => btn.setFillStyle(0x5555CC))
      btn.on('pointerout', () => btn.setFillStyle(0x4444AA))
      btn.on('pointerdown', () => this.onSelect(wallet.type))

      y += 90
    }
  }

  private onSelect(type: WalletType): void {
    // Remove interactivity from all buttons by disabling input on the scene
    this.input.enabled = false
    this.initChain(type)
  }

  private async initChain(type: WalletType): Promise<void> {
    try {
      this.setStatus('Connecting wallet...')
      await connectWallet(type)
      const pubkey = getPublicKey()!
      this.setStatus('Wallet connected')

      // Airdrop SOL in localnet mode
      if (getCluster() === 'localnet') {
        const conn = getConnection()
        const balance = await conn.getBalance(pubkey)
        if (balance < LAMPORTS_PER_SOL) {
          this.setStatus('Airdropping SOL...')
          const sig = await conn.requestAirdrop(pubkey, 10 * LAMPORTS_PER_SOL)
          await conn.confirmTransaction(sig)
        }
      }

      // Fetch or init PlayerState
      this.setStatus('Loading player state...')
      let playerState = await fetchPlayerState(pubkey)
      if (!playerState) {
        this.setStatus('Creating player account...')
        await initPlayer()
        await this.sleep(1500)
        playerState = await fetchPlayerState(pubkey)
        if (!playerState) throw new Error('Failed to create PlayerState')
      }
      cachePlayerState(playerState)

      // Fetch or init RunState
      this.setStatus('Loading run state...')
      let runState = await fetchRunState(pubkey)
      if (!runState) {
        this.setStatus('Creating run account...')
        await initRun()
        await this.sleep(1500)
        runState = await fetchRunState(pubkey)
        if (!runState) throw new Error('Failed to create RunState')
      }
      cacheRunState(runState)

      // If the RunState is still delegated, bring it back to base before proceeding.
      const delegated = await isRunStateDelegated(pubkey)
      if (delegated) {
        this.setStatus('Undelegating run state...')
        await undelegateRun()
        await waitForUndelegation(pubkey)
        runState = await fetchRunState(pubkey)
        if (runState) cacheRunState(runState)
      }

      // If a run was interrupted (active), recover it
      if (runState && runState.active) {
        this.setStatus('Finishing interrupted run...')
        try {
          await finishRun()
          await this.sleep(1500)
          playerState = await fetchPlayerState(pubkey)
          if (playerState) cachePlayerState(playerState)
          runState = await fetchRunState(pubkey)
          cacheRunState(runState)
        } catch (e) {
          console.warn('Failed to finish interrupted run:', e)
        }
      }

      this.setStatus('Ready!')
      this.scene.start('HubScene')

    } catch (err) {
      console.error('Boot failed:', err)
      this.setStatus(`Error: ${err instanceof Error ? err.message : String(err)}`)
      this.input.enabled = true
    }
  }

  private setStatus(msg: string): void {
    if (this.statusText) this.statusText.setText(msg)
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
